<template>
    <div data-component="Sheet"
         class="sheet-container"
         tabindex="0"
         :class="{'sheet-container--blur': blurBackdrop}"
         @click.self="backdropClick">
        <div class="sheet"
             :class="classes">
            <slot name="header">
                <div class="sheet-header">
                    <h4>
                        <slot name="header-title">
                            <template v-if="customTitleText">
                                {{ customTitleText }}
                            </template>
                            <template v-else-if="title">
                                {{ l(title) }}
                            </template>
                        </slot>
                    </h4>

                    <i v-if="loading"
                       class="fa-solid fa-spinner fa-spin"></i>
                </div>
            </slot>

            <!-- You can put something in the "body" slot if you want to be outside the .sheet-body container.
                    Otherwise, the default slot content goes inside the .sheet-body. -->
            <slot name="body">
                <div ref="sheet-body"
                     class="sheet-body"
                     :class="{
                         'sheet-body--padded': paddedBody,
                         'sheet-body--shaded': shadedBody
                     }">
                    <!-- Default slot -->
                    <slot></slot>
                </div>
            </slot>

            <slot name="footer">
                <!-- Show the footer if an OK or Cancel button is to be shown. -->
                <div v-if="ok || cancel"
                     class="sheet-footer">
                    <button v-if="cancel"
                            class="btn btn-lg btn-default cancel-btn sheet__cancel-btn"
                            @click.prevent="cancel">
                        {{ cancelLabelRaw ? cancelLabelRaw : l(cancelLabel) }}
                    </button>
                    <button v-if="ok"
                            class="btn btn-lg ok-btn"
                            :class="[`btn-${okColor}`]"
                            :disabled="okSpinner || !okEnabled"
                            @click.prevent="ok">
                        <i v-if="okSpinner"
                           class="fa-solid fa-spinner fa-spin" />
                        <template v-else>
                            {{ okLabelRaw ? okLabelRaw : l(okLabel) }}
                        </template>
                    </button>
                </div>
            </slot>
        </div>
    </div>
</template>

<script>

export default {
    name: 'Sheet',

    props: {
        // Set the size of the sheet.
        // Possible values are empty (default size), 'sm', 'lg', or 'xl'.
        size: {
            type: String,
            default: '',
        },
        height: {
            type: String,
            default: '',
        },

        // Add extra padding to the body to match the design of newer mockups?
        paddedBody: {
            type: Boolean,
            default: false,
        },

        // Shade the background of the body light grey to match the design of newer mockups?
        shadedBody: {
            type: Boolean,
            default: false,
        },

        // Icon to display in the header.
        // Give a FontAwesome icon name such as "fa-circle-exclamation"
        // This will be a .fa-solid
        icon: {
            type: String,
            default: 'fa-circle-exclamation',
        },
        // A key from the language file to use as the title in the header.
        title: {
            type: String,
            default: null,
        },
        // A string of text to display as the the title in the header instead of the :title lang key.
        // Note that this is still escaped, so HTML is not allowed.
        // Overrides this.title if both are supplied.
        customTitleText: {
            type: String,
            default: null,
        },
        // If an 'ok' function is given, an OK button will be shown.
        ok: {
            type: Function,
            default: null,
        },
        okColor: {
            type: String,
            default: 'primary',
        },
        okEnabled: {
            type: Boolean,
            default: true,
        },
        // A key from the language file to use as the label for the OK button.
        okLabel: {
            type: String,
            default: 'OK',
        },
        // A raw string of text to display as the label for the OK button.
        // Overrides this.okLabel if both are supplied.
        okLabelRaw: {
            type: String,
            default: null,
        },
        // If true the OK button will show a loading spinner instead of the text and be disabled.
        okSpinner: {
            type: Boolean,
            default: false,
        },
        // If a 'cancel' function is given a Cancel button will be shown.
        cancel: {
            type: Function,
            default: null,
        },
        cancelLabel: {
            type: String,
            default: 'CANCEL',
        },
        // A raw string of text to display as the label for the cancel button.
        // Overrides this.cancelLabel if both are supplied.
        cancelLabelRaw: {
            type: String,
            default: null,
        },
        cancelOnBackdropClick: {
            type: Boolean,
            default: true,
        },
        /**
         * Show a loading indicator in the top right of the sheet header?
         */
        loading: {
            type: Boolean,
            default: false,
        },

        /**
         * If true the backdrop of the sheet will be darker and blur the content behind it.
         */
        blurBackdrop: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        classes() {
            let classes = [];
            if (this.size) {
                classes.push(`sheet--${this.size}`);
            }
            if (this.height) {
                classes.push(`sheet--${this.height}`);
            }

            return classes;
        },
    },

    mounted() {
        setTimeout(() => {
            if (this.$el) {
                this.$el.focus();
            }
        }, 100);
    },

    methods: {
        backdropClick() {
            if (this.cancelOnBackdropClick) {
                this.cancel();
            }
        },
    },
};
</script>

<style lang="less">
@import (reference) "../../../less/variables";
@import (reference) "../../../less/dtui/dtui";

.sheet-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: @vue-modal-z-index + 1;
    padding: 0;

    display: flex;
    align-items: stretch;
    justify-content: right;

    max-height: 100%;
    max-width: 100%;
}

.sheet-container--blur {
    background: rgba(29, 29, 29, 0.6);

    // Use a darker background and blur the content behind it.
    backdrop-filter: blur(20px);

    // Bring the solid backdrop modals up higher than others.
    z-index: @vue-modal-z-index + 10;
}

.sheet {
    width: @sheet-width-default;

    background: @white;
    display: flex;
    flex-direction: column;

    box-shadow: 0 5px 15px rgba(0, 0, 0, .5);

    &.sheet--sm {
        width: @sheet-width-small;
    }

    &.sheet--lg {
        width: @sheet-width-large;
    }

    &.sheet--xl {
        width: @sheet-width-xl;
    }
}

.sheet-header {
    flex-grow: 0;
    flex-shrink: 0;
    background: @white;
    color: @font-color;
    overflow: hidden;
    white-space: nowrap;
    min-height: @sheet-header-height; // min-height not height because sheet-tabs are larger
    display: flex;
    align-items: center;
    padding: 0 @gutter-width;

    h4 {
        flex-grow: 1;
        margin: 0;
        font-size: 18px;
        font-weight: 600;
        min-width: 0;
        line-height: @sheet-header-height;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    border-bottom: 1px solid @btn-default-border;

    .sheet__cancel-btn {
        text-decoration: underline;
    }
}

.sheet-body {
    background: @white;
    flex-grow: 1;
    flex-shrink: 1;
    overflow: auto;
    padding: @gutter-width;
    font-size: 15px;
    position: relative;
    scroll-behavior: smooth;

    &--padded {
        padding: 25px;
    }

    &--shaded {
        background: @modal-body-bg--grey;
    }
}

.sheet-footer {
    display: flex;
    align-items: center;
    flex-grow: 0;
    flex-shrink: 0;

    background-color: @dt-grey-neutral-100;

    padding: @gutter-width;
    border-top: 1px solid @btn-default-border;

    .btn {
        margin: 0 !important;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    // Make the sheet buttons always vertical regardless of screen size.
    &--vertical {
        display: flex;
        flex-direction: column;
        height: auto;

        .btn {
            width: 100%;
        }
    }
}

// Normally a sheet is displayed like:
// .sheet
//     .sheet-header
//     .sheet-body
//     .sheet-footer
//
// But in a tabbed sheet you would get:
// .sheet
//     .sheet-header
//     .sheet-tab-body
//         .sheet-body
//         .sheet-footer
//
// We need to make sheet-tab-body play nicely with the expected flex setup
// of the sheet contents.
.sheet-tab-body {
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

@media (max-width: @mobile-max) {
    .sheet {
        &.sheet--sm,
        &.sheet--lg,
        &.sheet--xl {
            width: 100%;
        }
    }

    .sheet-container {
        // The height of the bar is 64px. Show the bar and a small gap underneath in the background.
        padding: 75px 0 0 0;
    }

    .sheet-header {
        min-height: 49px; // Taking off 1px makes the vertical centering better.
    }

    .sheet-body {
        padding: @gutter-width-mobile;

        &--padded {
            padding: 20px;
        }
    }

    .sheet-footer {
        padding: @gutter-width-mobile;
        display: flex;
        // Display buttons in a column. Reverse the order because the most important ones are normally on the right.
        flex-direction: column-reverse;
        grid-gap: @gutter-width-mobile;

        .btn {
            margin: 0 !important;
            width: 100%;
            max-width: 100%;
        }

        .cancel-btn {
            // Move the cancel button to be the last button in the list.
            order: -100;
        }
    }
}

@media (min-width: @non-mobile-min) {
    .sheet-footer {
        display: flex;
        grid-gap: @gutter-width; // Spacing between footer buttons.

        &:not(.sheet-footer--vertical) {
            height: @sheet-footer-height;
            justify-content: flex-end; // Use the .cancel-btn class to but the left button on the left.

            .cancel-btn {
                // This makes the cancel button be all the way on the left on desktop.
                // It doesn't affect the styling, only the position.
                margin-right: auto !important;
            }
        }
    }
}
</style>
